import React, { useMemo, useState } from 'react';
import UnlinkCrossIcon from '../../../../../../assets/Svg/UnlinkCrossIcon';
import CircularSelect from '../../../../../../common/MagicUpload/components/CircularSelect/CircularSelect';
import { OrgEntity } from '../../../../../../store/dashboard/financials/entityRationaleServices';
import { KEYBOARD_EVENTS } from '../../../../../../utils/constants/commonConstants';
import { CLASSIFICATION_OPTIONS, UNCLASSIFIED_DISPLAY_LABEL } from './constants';
import { GrainEdit, StagedEdit } from './types';
import styles from './EntityRowEditControls.module.scss';

const CLASSIFICATION_LABELS = CLASSIFICATION_OPTIONS.map((o) => o.label);

interface Props {
    entityId: number;
    entityName: string;
    currentClassificationId: number;
    /** Display-formatted rationale pre-fill (chip label or served rationale). */
    currentRationale: string | null;
    /** Raw grain rationale for this row: parsed bucketKey for kind-1, null for kind-2. */
    rowCurrentRationale: string | null;
    /** Grain-scoped staged state for this row. */
    grainStaged: GrainEdit | undefined;
    rationaleOptions: string[];
    onStage: (entityId: number, patch: Partial<StagedEdit>) => void;
    /** Org-wide entity list fetched once on edit-mode entry; used for bubble autocomplete. */
    orgEntities?: OrgEntity[];
}

/** Name column fragment: entity bubble with × / click-to-open CircularSelect search + org-entity autocomplete. */
export function EntityBubbleCell({
    entityId,
    entityName,
    currentClassificationId,
    rowCurrentRationale,
    rowGrainKey,
    grainStaged,
    onStage,
    onRestore,
    orgEntities = [],
}: Pick<
    Props,
    'entityId' | 'entityName' | 'currentClassificationId' | 'rowCurrentRationale' | 'grainStaged' | 'onStage' | 'orgEntities'
> & {
    rowGrainKey: string;
    onRestore: (entityId: number, rowGrainKey: string) => void;
}) {
    const [editingName, setEditingName] = useState(false);

    const isUnlinked = grainStaged?.unlink === true;

    const handleUnlink = () => {
        onStage(entityId, { currentClassificationId, currentRationale: rowCurrentRationale, unlink: true });
    };

    // Chip shows target name when a re-link is staged, otherwise the rename/original name.
    const chipDisplayName = grainStaged?.targetEntityName ?? grainStaged?.entityName ?? entityName;

    const startEditing = () => setEditingName(true);

    // When an unlink is staged the row's own entity is a valid "undo" target; exclude it otherwise
    // to prevent a no-op self-re-link (UW rejects targetEntityId === entityId with 400).
    const entityOptions = useMemo(
        () => orgEntities.filter((e) => isUnlinked || e.entityId !== entityId).map((e) => e.displayName ?? ''),
        [orgEntities, entityId, isUnlinked],
    );

    const handleEntitySelect = async (value: string): Promise<boolean> => {
        const matched = orgEntities.find((e) => e.displayName === value);
        if (matched?.entityId === entityId && isUnlinked) {
            // Picking the row's own entity while an unlink is staged → undo the unlink.
            onRestore(entityId, rowGrainKey);
        } else if (matched) {
            onStage(entityId, {
                currentClassificationId,
                currentRationale: rowCurrentRationale,
                targetEntityId: matched.entityId,
                targetEntityName: matched.displayName,
            });
        } else {
            onStage(entityId, { currentClassificationId, currentRationale: rowCurrentRationale, entityName: value });
        }
        setEditingName(false);
        return true;
    };

    if (isUnlinked) {
        return (
            <span className={styles.BubbleWrapper}>
                <span
                    onKeyDown={(e) => {
                        if (e.key === KEYBOARD_EVENTS.ESCAPE) setEditingName(false);
                    }}
                >
                    <CircularSelect
                        option={entityOptions}
                        onSelect={handleEntitySelect}
                        selectedOption={grainStaged?.targetEntityName ?? grainStaged?.entityName ?? ''}
                        placeholder="Select Entity"
                        searchable
                        allowAddNew
                        addNewText="Add new entity"
                        otherOptionPlaceholderText="Enter entity name..."
                        neutralPlaceholder
                        portalPanel
                        selectClassName={styles.CfbPill}
                        onClear={() => onRestore(entityId, rowGrainKey)}
                    />
                </span>
            </span>
        );
    }

    return (
        <span className={styles.BubbleWrapper}>
            {editingName ? (
                // onBlur is intentionally absent: the portaled panel lives outside this
                // span's DOM subtree, so blur fires spuriously when focus moves into the
                // portal — tearing down the select before add-new can complete.
                // CircularSelect closes on outside-mousedown; handleEntitySelect and
                // the Escape handler below are the two exit paths.
                <span
                    onKeyDown={(e) => {
                        if (e.key === KEYBOARD_EVENTS.ESCAPE) setEditingName(false);
                    }}
                >
                    <CircularSelect
                        option={entityOptions}
                        onSelect={handleEntitySelect}
                        selectedOption={chipDisplayName}
                        placeholder="Select Entity"
                        searchable
                        allowAddNew
                        addNewText="Add new entity"
                        otherOptionPlaceholderText="Enter entity name..."
                        selectClassName={styles.CfbPill}
                        portalPanel
                    />
                </span>
            ) : (
                <span className={styles.EntityBubble} onClick={startEditing}>
                    <span
                        className={styles.BubbleLabel}
                        title={chipDisplayName}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) startEditing();
                        }}
                    >
                        {chipDisplayName}
                    </span>
                    <button
                        type="button"
                        className={styles.UnlinkBtn}
                        aria-label="Remove entity"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleUnlink();
                        }}
                    >
                        <UnlinkCrossIcon />
                    </button>
                </span>
            )}
        </span>
    );
}

/** Tagging Rational column fragment: rationale CircularSelect with a clear affordance. */
export function RationaleCell({
    entityId,
    currentClassificationId,
    currentRationale,
    rowCurrentRationale,
    grainStaged,
    rationaleOptions,
    onStage,
}: Omit<Props, 'entityName' | 'orgEntities'>) {
    const displayedRationale = grainStaged?.clearRationale ? '' : grainStaged?.rationale ?? currentRationale ?? '';
    const showClear = !!(grainStaged?.rationale ?? currentRationale) && !grainStaged?.clearRationale;

    return (
        <CircularSelect
            option={rationaleOptions}
            onSelect={async (v) => {
                onStage(entityId, { currentClassificationId, currentRationale: rowCurrentRationale, rationale: v });
                return true;
            }}
            selectedOption={displayedRationale}
            placeholder="Select Rational"
            searchable
            allowAddNew
            addNewText="Add rationale"
            otherOptionPlaceholderText="Enter rationale..."
            neutralPlaceholder
            portalPanel
            selectClassName={styles.CfbPill}
            onClear={
                showClear
                    ? () => onStage(entityId, { currentClassificationId, currentRationale: rowCurrentRationale, clearRationale: true })
                    : undefined
            }
        />
    );
}

/** Classification column fragment: classification CircularSelect (fixed 8 buckets, not additive). */
export function ClassificationCell({
    entityId,
    currentClassificationId,
    rowCurrentRationale,
    grainStaged,
    onStage,
}: Pick<Props, 'entityId' | 'currentClassificationId' | 'rowCurrentRationale' | 'grainStaged' | 'onStage'>) {
    const effectiveClassificationId = grainStaged?.classificationId ?? currentClassificationId;
    const isCleared = grainStaged?.clearClassification === true;

    // No effective classification: staged clear OR id not in the 8 real buckets (null / legacy id-2).
    const mappedLabel = CLASSIFICATION_OPTIONS.find((o) => o.id === effectiveClassificationId)?.label;

    // Show × clear only when a real bucket is selected and it hasn't been cleared yet.
    const showClear = !isCleared && !!mappedLabel;

    return (
        <CircularSelect
            option={CLASSIFICATION_LABELS}
            onSelect={async (label) => {
                const option = CLASSIFICATION_OPTIONS.find((o) => o.label === label)!;
                onStage(entityId, {
                    currentClassificationId,
                    currentRationale: rowCurrentRationale,
                    classificationId: option.id,
                    clearClassification: undefined,
                });
                return true;
            }}
            selectedOption={isCleared || !mappedLabel ? '' : mappedLabel}
            placeholder="Select Classification"
            searchable
            allowAddNew={false}
            neutralPlaceholder
            portalPanel
            selectClassName={styles.CfbPill}
            onClear={
                showClear
                    ? () =>
                          onStage(entityId, {
                              currentClassificationId,
                              currentRationale: rowCurrentRationale,
                              clearClassification: true,
                              classificationId: undefined,
                          })
                    : undefined
            }
        />
    );
}
